import { usePay } from '~/composables/usePay'

/**
 * PayerMax 前置组件预热 + session 缓存
 *
 * 作用：商品列表加载后即可后台预热 SDK + session（仿 master-h5「获取商品后静默渲染」），
 * 等用户打开支付弹窗时直接用缓存，减少 loading 等待。
 *
 * 缓存：按 rsp_id 缓存 session，过期判断沿用 master 的 payExpiredTime（默认 1 小时，
 * 可被 localStorage.payExpiredTime 覆盖），超过则重新创建。
 */

// PayerMax SDK CDN 地址
const PAYERMAX_SDK_URL = 'https://cdn.payermax.com/dropin/js/pmdropin.min.js'

interface CachedSession {
  session: any
  timestamp: number
}

// 按支付类型分桶缓存（localStorage 级，跨刷新/支付跳转回调仍保留）：
// PayerMax 的 session 在后端建立时绑定了 rsp_id 对应商品的类型（订阅/一次性），
// 订阅 session 不能用于一次性支付（会报 subscriptionPlan/mitManagementUrl invalid），反之亦然，
// 故 order 与 subscription 各存一份，各自 60 分钟复用，同类型下不同商品共用一份。
// 下单走 /order/create（createDropInOrder）、订阅计划取自商品 subscription_plan_config.payermax、价格靠 totalAmount 传
const DROP_IN_SESSION_KEY_PREFIX = '__payermaxDropInSession_'
// paymentType 归一化为两桶：subscription / order（默认 order）
const sessionKeyOf = (paymentType?: string) =>
  DROP_IN_SESSION_KEY_PREFIX + (paymentType === 'subscription' ? 'subscription' : 'order')
const getCachedEntry = (paymentType?: string): CachedSession | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(sessionKeyOf(paymentType))
    return raw ? JSON.parse(raw) as CachedSession : null
  } catch {
    return null  // 解析失败（脏数据）当无缓存
  }
}
const setCachedEntry = (entry: CachedSession | null, paymentType?: string): void => {
  if (typeof window === 'undefined') return
  const key = sessionKeyOf(paymentType)
  if (entry) {
    localStorage.setItem(key, JSON.stringify(entry))
  } else {
    localStorage.removeItem(key)
  }
}

// in-flight 去重：同类型桶并发建 session 只请求一次（多 PaymentButton 实例 google/apple + pay-popup + Card 全屏共享）
// 对齐 master usePaymentSession 的 inflightSessions；缺此去重会导致同桶并发重复请求 create-drop-in-session
const inflightSessions = new Map<string, Promise<void>>()

// 动态加载 PayerMax SDK（带 window.PMdropin 单例判断）
export const loadPayermaxSdk = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false)
      return
    }
    if ((window as any).PMdropin) {
      resolve(true)
      return
    }
    const script = document.createElement('script')
    script.src = PAYERMAX_SDK_URL
    script.async = true
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.head.appendChild(script)
  })
}

// 过期时间（对齐 master：默认 1 小时，可被 localStorage.payExpiredTime 覆盖）
const getPayExpiredTime = (): number => {
  let payExpiredTime = 60 * 60 * 1000
  if (typeof window !== 'undefined') {
    const local = localStorage.getItem('payExpiredTime')
    if (local) {
      const parsed = Number(local)
      if (!isNaN(parsed) && parsed > 0) payExpiredTime = parsed
    }
  }
  return payExpiredTime
}

// 支付渠道列表缓存（全局通用，不依赖 rsp_id，请求一次后一直复用）
const getOptionsCache = (): { data: any[] } | null => {
  if (typeof window === 'undefined') return null
  return (window as any).__payermaxOptionsCache || null
}

export function usePaymentPreload() {
  const { createDropInSession } = usePay()

  // 获取支付渠道列表（带缓存）
  // forceRefresh=true：强制重新请求并更新缓存（productsPopup 每次打开时刷新，保证配置及时）
  // forceRefresh=false：命中缓存直接复用（pay-popup 打开时用，避免重复请求）
  const getPaymentOptions = async (forceRefresh = false): Promise<any[]> => {
    const cached = getOptionsCache()
    if (!forceRefresh && cached) return cached.data
    try {
      const res: any = await clientFetch('/api/pay/get-payment-options')
      if (res.code === 0) {
        ;(window as any).__payermaxOptionsCache = { data: res.data }
        return res.data
      }
    } catch (e) {
      console.error('[getPaymentOptions] 获取支付渠道失败:', e)
    }
    // 请求失败时回退用旧缓存（若有）
    return cached?.data || []
  }

  // 判断渠道列表里有没有 PayerMax 前置组件渠道（google/apple/card），有才需要预热建 session
  const hasPayermaxChannel = (options: any[]): boolean => {
    if (!options || !options.length) return false
    return options.some(item => {
      const code = (item.channel_code || '').toLowerCase()
      return code.includes('google') || code.includes('apple') || code.includes('card')
    })
  }

  // 取对应支付类型桶的 session（未过期才返回）；同类型下不同商品共用一份
  const getCachedSession = (paymentType?: string) => {
    const cached = getCachedEntry(paymentType)
    if (!cached) return null
    if (Date.now() - cached.timestamp > getPayExpiredTime()) {
      setCachedEntry(null, paymentType)
      return null
    }
    return cached.session
  }

  // 预热：加载 SDK + 创建 session，并按支付类型分桶缓存（静默，失败不抛错）；对应桶已有未过期缓存则跳过
  const preloadDropIn = async (rsp_id: string, extra: any = {}, paymentType?: string) => {
    if (!rsp_id) return
    // 对应类型桶已有未过期缓存则跳过
    if (getCachedSession(paymentType)) {
      loadPayermaxSdk()
      return
    }
    // 同桶已有进行中的建 session 任务 → 复用，避免并发重复请求
    const bucketKey = sessionKeyOf(paymentType)
    const existing = inflightSessions.get(bucketKey)
    if (existing) return existing
    const task = (async () => {
      try {
        const [sdkLoaded, sessionRes] = await Promise.all([
          loadPayermaxSdk(),
          createDropInSession(rsp_id, extra)
        ])
        if (sdkLoaded && sessionRes && (sessionRes as any).code === 0) {
          setCachedEntry({
            session: (sessionRes as any).data,
            timestamp: Date.now()
          }, paymentType)
        }
      } catch (e) {
        console.error('[preloadDropIn] 预热失败:', e)
      } finally {
        inflightSessions.delete(bucketKey)
      }
    })()
    inflightSessions.set(bucketKey, task)
    return task
  }

  // 取/建对应类型桶的 session：命中缓存零请求；未命中经 preloadDropIn（内含 in-flight 去重）请求后再取。
  // 收敛 PaymentButton 里重复的「getCachedSession → preloadDropIn → getCachedSession」三连，session 编排单一出处（对齐 master usePaymentSession）
  const getOrCreateSession = async (paymentType: string, rsp_id: string, extra: any = {}) => {
    const cached = getCachedSession(paymentType)
    if (cached) return cached
    await preloadDropIn(rsp_id, extra, paymentType)
    return getCachedSession(paymentType)
  }

  // 清掉对应支付类型桶的缓存（PM 返回 token 失效时用，强制下次重建）
  const clearCachedSession = (paymentType?: string): void => {
    setCachedEntry(null, paymentType)
  }

  return {
    loadPayermaxSdk,
    getCachedSession,
    preloadDropIn,
    getOrCreateSession,
    clearCachedSession,
    getPaymentOptions,
    hasPayermaxChannel
  }
}
